import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Wallet,
  Clock,
  Sun,
  Umbrella,
  AlertCircle,
  TrendingUp,
  Gift,
  AlertTriangle,
} from 'lucide-react';
import { payrollService } from '@/services/payroll.service';
import { paidHoursService } from '@/services/paid-hours.service';
import { penaltyService } from '@/services/penalty.service';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { StatTile } from '@/components/ui/stat-tile';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SalaryDetailModal } from '@/components/payroll/SalaryDetailModal';
import { RateHistorySection } from '@/components/payroll/RateHistorySection';

function formatCurrency(val: string | number): string {
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatHours(mins: number): string {
  if (!mins || mins <= 0) return '0h 0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Backfilled baseline rows carry 1900-01-01 — a sentinel, not a real date. */

function buildYears(): number[] {
  const now = new Date().getFullYear();
  return [now, now - 1, now - 2];
}


interface Props {
  userId: string;
  /** Shown when adding or editing a rate period, so the dialog names who it is for. */
  employeeName: string;
}

export function StaffSalaryTab({ userId, employeeName }: Props) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [detailOpen, setDetailOpen] = useState(false);

  const { data: financial, isLoading: finLoading } = useQuery({
    queryKey: ['payroll', 'financial', userId],
    queryFn: () => payrollService.getFinancialData(userId),
  });

  const { data: record, isLoading: recLoading } = useQuery({
    queryKey: ['payroll', 'salary', userId, year, month],
    queryFn: () => payrollService.getStaffMonthSalary(userId, year, month),
  });

  const firstDayStr = `${year}-${String(month).padStart(2, '0')}-01`;
  // Not toISOString() — that shifts local midnight to UTC and lands on the 30th
  // in IST, hiding pending items dated on the month's last day.
  const lastDayStr  = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;

  const { data: pendingPaidHoursData } = useQuery({
    queryKey: ['paid-hours', 'pending-check', userId, year, month],
    queryFn: () =>
      paidHoursService.list({
        user_id:   userId,
        status:    'pending',
        date_from: firstDayStr,
        date_to:   lastDayStr,
        limit:     100,
        page:      1,
      }),
  });
  const pendingPaidCount = pendingPaidHoursData?.pagination.total ?? 0;
  const pendingPaidHrs   = pendingPaidHoursData?.data.reduce((s, r) => s + parseFloat(r.hours), 0) ?? 0;

  const { data: pendingPenaltiesData } = useQuery({
    queryKey: ['penalties', 'pending-check', userId, year, month],
    queryFn: () =>
      penaltyService.list({ user_id: userId, status: 'pending', page: 1, limit: 100 }),
  });
  const pendingPenaltyCount = pendingPenaltiesData?.pagination.total ?? 0;
  const pendingPenaltyMins  = (pendingPenaltiesData?.data ?? [])
    .filter((p) => p.attendance_date >= firstDayStr && p.attendance_date <= lastDayStr)
    .reduce((s, p) => s + p.penalty_minutes, 0);

  const gross = record ? parseFloat(record.gross_amount) : 0;
  const net   = record ? parseFloat(record.net_amount) : 0;
  const loanDed    = record ? parseFloat(record.loan_deduction)    : 0;
  const advDed     = record ? parseFloat(record.advance_deduction) : 0;
  const paidHoursAmt  = record ? parseFloat((record.paid_hours_amount  as string | undefined) ?? '0') : 0;
  const paidHoursMins = record ? ((record.paid_hours_minutes as number | undefined) ?? 0) : 0;

  // total_payable_minutes and gross_amount now arrive already net of the penalty hours,
  // so both are shown straight from the record. This page used to subtract the penalty
  // itself while the payroll list and the detail modal did not, which is why the same
  // employee read differently depending on which screen you opened.
  const netPayableHours = record ? formatHours(record.total_payable_minutes) : '—';

  // What those penalty hours cost, priced at the month's own rate. Derived rather than
  // read from penalty_deduction: that column now carries only the residual money penalty,
  // which is zero whenever penalties are charged per hour.
  const penaltyRate  = record ? parseFloat(record.per_hour_rate_snapshot) : 0;
  const penaltyValue = record ? (record.penalty_minutes / 60) * penaltyRate : 0;

  const noRateConfigured = !financial?.per_hour_rate || parseFloat(financial.per_hour_rate) === 0;

  // Sunday pay: each Sunday's first 9h capped at the configured rate, that day's excess at 1x
  // — computed server-side per individual Sunday (payroll.service.ts), not re-derived here.
  const sundayCapped = record ? record.sunday_capped_minutes : 0;
  const sundayExcess = record ? record.sunday_excess_minutes : 0;
  const sundayRate = record && sundayCapped > 0
    ? (record.sunday_effective_minutes - sundayExcess) / sundayCapped
    : 2;

  return (
    <div className="space-y-4">
      {/* Financial Summary */}
      {!finLoading && financial && (
        <Card>
          <SectionHeader icon={<Wallet />} title="Financial Summary" />
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <FinRow
                label="Per Hour Rate"
                value={financial.per_hour_rate ? `₹${parseFloat(financial.per_hour_rate).toFixed(2)}/hr` : '—'}
              />
              <FinRow label="PAN" value={financial.pan_number ?? '—'} />
              <FinRow label="Bank Account" value={financial.bank_account_number ?? '—'} />
              <FinRow label="IFSC Code" value={financial.ifsc_code ?? '—'} />
              <FinRow label="Name on Account" value={financial.name_on_account ?? '—'} />
              <FinRow label="UPI ID" value={financial.upi_id ?? '—'} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rate History */}
      <RateHistorySection userId={userId} employeeName={employeeName} />

      {/* Monthly Salary Section */}
      <Card>
        <SectionHeader
          icon={<TrendingUp />}
          title="Monthly Salary Overview"
          action={
            <>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="h-9 w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {buildYears().map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger className="h-9 w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((name, idx) => (
                    <SelectItem key={idx + 1} value={String(idx + 1)}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          }
        />
        <CardContent className="p-4 space-y-5">

      {recLoading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : !record ? (
        <div className="rounded-lg border border-border/60 bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No attendance, leave or paid hours recorded for {MONTH_SHORT[month - 1]} {year}.
        </div>
      ) : (
        <>
          {/* ── Pending paid hours warning ── */}
          {pendingPaidCount > 0 && (
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-300/60 bg-amber-50 px-3.5 py-3 text-sm text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                <strong>{pendingPaidCount} pending paid hours record{pendingPaidCount > 1 ? 's' : ''}</strong> ({pendingPaidHrs.toFixed(1)}h total) are waiting for approval and are <strong>not included</strong> in this salary. Approve them under <strong>Attendance → Paid Hours</strong> and the figures here update on their own.
              </span>
            </div>
          )}

          {/* ── Pending penalty warning ── */}
          {pendingPenaltyCount > 0 && (
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-300/60 bg-amber-50 px-3.5 py-3 text-sm text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                <strong>{pendingPenaltyCount} pending penalty record{pendingPenaltyCount > 1 ? 's' : ''}</strong> ({formatHours(pendingPenaltyMins)} total) are waiting for approval and are not yet deducted from this salary. Go to <strong>HRMS → Penalty Approvals</strong> to review.
              </span>
            </div>
          )}

          {/* ── No-rate warning ── */}
          {noRateConfigured && (
            <div className="flex items-start gap-2.5 rounded-lg border border-amber-300/60 bg-amber-50 px-3.5 py-3 text-sm text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                No hourly rate is configured for this employee. Gross salary will be <strong>₹0</strong> until a rate is set in Financial Summary above.
              </span>
            </div>
          )}

          {/* Two-column hero */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">

            {/* Left — Net Salary (dark teal, clickable) */}
            <div
              className="relative cursor-pointer overflow-hidden rounded-2xl bg-gradient-to-br from-teal-800 via-teal-700 to-teal-600 px-6 py-6 text-white shadow-md"
              onClick={() => setDetailOpen(true)}
            >
              <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
              <div className="relative flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-teal-300">
                    Net Salary
                  </p>
                  <p className="mt-1 text-4xl font-bold">{formatCurrency(net)}</p>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm text-teal-100">
                    <span>Gross {formatCurrency(gross)}</span>
                    <span>·</span>
                    <span>Loan {formatCurrency(loanDed)}</span>
                    <span>·</span>
                    <span>Advance {formatCurrency(advDed)}</span>
                    <span>·</span>
                    <span>Payable {netPayableHours}</span>
                    <span>·</span>
                    <span>₹{parseFloat(record.per_hour_rate_snapshot).toFixed(2)}/hr</span>
                  </div>
                </div>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20">
                  <Wallet className="h-5 w-5" />
                </div>
              </div>
            </div>

            {/* Right — Total Payable Hours */}
            <div className="rounded-2xl border border-border/60 bg-card px-5 py-5 lg:min-w-[260px]">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                Total Payable Hours
              </p>
              <p className="mt-1 text-3xl font-bold">{netPayableHours}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Work + Sunday + Grace + Leave{paidHoursMins > 0 ? ' + Company Paid' : ''}
                {' − Penalty'}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-1 text-sm">
                <span className="font-medium text-teal-700">{formatHours(record.working_minutes)}</span>
                <span className="text-muted-foreground">+</span>
                <span className="font-medium text-amber-700">{formatHours(record.sunday_effective_minutes)}</span>
                <span className="text-muted-foreground">+</span>
                <span className="font-medium text-emerald-700">{formatHours(record.grace_minutes)}</span>
                <span className="text-muted-foreground">+</span>
                <span className="font-medium text-violet-700">{formatHours(record.paid_leave_minutes)}</span>
                {paidHoursMins > 0 && (
                  <>
                    <span className="text-muted-foreground">+</span>
                    <span className="font-medium text-teal-600">{formatHours(paidHoursMins)}</span>
                  </>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1 text-sm">
                <span className="font-medium text-rose-700">−{formatHours(record.penalty_minutes)}</span>
              </div>
            </div>
          </div>

          {/* Hours Breakdown */}
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Hours Breakdown
            </p>
            <div className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${paidHoursMins > 0 ? 'lg:grid-cols-6' : 'lg:grid-cols-5'}`}>
              <StatTile label="Working Hours"     value={formatHours(record.working_minutes)}          sub="Mon–Sat punch"    icon={Clock}       tone="sky" />
              <StatTile label="Grace Hours"       value={formatHours(record.grace_minutes)}            sub="Bonus (day rate)" icon={Clock}       tone="emerald" />
              <StatTile
                label="Sunday Hours (2X)"
                value={formatHours(record.sunday_effective_minutes)}
                sub={sundayExcess > 0
                  ? `${formatHours(sundayCapped)} ×${sundayRate.toFixed(0)}x + ${formatHours(sundayExcess)} ×1x`
                  : `${formatHours(sundayCapped)} ×${sundayRate.toFixed(0)}x`}
                icon={Sun}
                tone="amber"
              />
              <StatTile label="Paid Leave"        value={formatHours(record.paid_leave_minutes)}       sub="Approved leaves"  icon={Umbrella}    tone="violet" />
              <StatTile label="Penalty"           value={record.penalty_minutes > 0 ? `−${formatHours(record.penalty_minutes)}` : formatHours(record.penalty_minutes)} sub={record.penalty_minutes > 0 ? (penaltyValue > 0 ? `−${formatCurrency(penaltyValue)}` : 'Rate not set') : '—'} icon={AlertCircle} tone="rose" />
              {paidHoursMins > 0 && (
                <StatTile label="Company Paid" value={formatHours(paidHoursMins)} sub={paidHoursAmt > 0 ? `+${formatCurrency(paidHoursAmt)}` : 'Approved'} icon={Gift} tone="teal" />
              )}
            </div>
          </div>

          {/* Company paid hours credit row */}
          {paidHoursAmt > 0 && (
            <div className="flex items-center justify-between rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm dark:border-teal-500/20 dark:bg-teal-500/10">
              <div className="flex items-center gap-2 font-medium text-teal-700 dark:text-teal-300">
                <Gift className="h-4 w-4" /> Company Paid Hours Credit ({formatHours(paidHoursMins)})
              </div>
              <span className="font-bold text-teal-700 dark:text-teal-300">+{formatCurrency(paidHoursAmt)}</span>
            </div>
          )}

          {/* Penalty row — the hours already removed from Total Payable above, priced */}
          {record.penalty_minutes > 0 && (
            <div className="flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm dark:border-rose-500/20 dark:bg-rose-500/10">
              <div className="flex items-center gap-2 font-medium text-rose-700 dark:text-rose-300">
                <AlertCircle className="h-4 w-4" />
                Penalty Hours Removed ({formatHours(record.penalty_minutes)})
              </div>
              <span className="font-bold text-rose-700 dark:text-rose-300">
                {penaltyValue > 0 ? `−${formatCurrency(penaltyValue)}` : '−₹0 (set hourly rate)'}
              </span>
            </div>
          )}
        </>
      )}

        </CardContent>
      </Card>{/* end Monthly Salary Section */}

      {record && (
        <SalaryDetailModal record={record} open={detailOpen} onClose={() => setDetailOpen(false)} />
      )}
    </div>
  );
}

function FinRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-medium mt-0.5">{value}</p>
    </div>
  );
}

